import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Save, Plus, Trash2, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { adminGet, adminPost, adminPut, adminDelete, formatApiError, resolveAssetUrl } from "../../lib/api";
import MediaPicker from "./MediaPicker";

const KINDS = [
  { v: "lumber", label: "Lumber (Featured)" },
  { v: "steel", label: "Steel (Featured)" },
  { v: "secondary", label: "Secondary Service" },
];

const ICONS = ["Layers3", "Hammer", "Cog", "Truck", "ShieldCheck", "Wrench", "TreePine"];

const blank = () => ({
  id: null, kind: "secondary", title: "", body: "", features: [], image_url: "", icon: "Cog", order: 0,
});

const ServicesEditor = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await adminGet("/services"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSave = async () => {
    if (!editing.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const body = { ...editing, features: (editing.features || []).filter(Boolean) };
      if (editing.id) await adminPut(`/services/${editing.id}`, body);
      else await adminPost("/services", body);
      await load();
      setEditing(null);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await adminDelete(`/services/${id}`);
      setItems((s) => s.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const grouped = {
    lumber: items.filter((s) => s.kind === "lumber"),
    steel: items.filter((s) => s.kind === "steel"),
    secondary: items.filter((s) => s.kind === "secondary"),
  };

  return (
    <div data-testid="admin-services-page">
      <header className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="font-display text-[11px] uppercase tracking-[0.3em] text-[#0A192F]/60">Catalog</div>
          <h1 className="mt-2 font-display font-black text-4xl text-[#0A192F] tracking-tighter">
            Services ({items.length})
          </h1>
        </div>
        <button
          onClick={() => setEditing(blank())}
          data-testid="admin-services-add"
          className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A192F] px-6 py-3 font-display text-xs uppercase tracking-[0.25em] font-bold hover:bg-[#0A192F] hover:text-white transition-colors"
        >
          <Plus size={14} /> Add Service
        </button>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#0A192F]/40">
          <Loader2 className="animate-spin mr-2" size={18} /> Loading…
        </div>
      ) : (
        <div className="space-y-12">
          {KINDS.map((k) => (
            <section key={k.v}>
              <h2 className="font-display font-bold text-sm uppercase tracking-[0.25em] text-[#0A192F] mb-4 border-b border-gray-200 pb-2">
                {k.label}
              </h2>
              {grouped[k.v].length === 0 ? (
                <div className="text-sm text-[#0A192F]/50 italic">No items in this category yet.</div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {grouped[k.v].map((s) => (
                    <div
                      key={s.id}
                      data-testid={`admin-service-item-${s.id}`}
                      className="bg-white border border-gray-200 p-5"
                    >
                      <div className="font-display font-bold text-[#0A192F]">{s.title}</div>
                      <div className="mt-2 text-sm text-[#0A192F]/70 line-clamp-3">{s.body}</div>
                      {s.features?.length > 0 && (
                        <div className="mt-3 text-[11px] text-[#0A192F]/50">{s.features.length} feature{s.features.length !== 1 ? "s" : ""}</div>
                      )}
                      <div className="mt-4 flex gap-2">
                        <button
                          onClick={() => setEditing({ ...s, features: s.features || [] })}
                          data-testid={`admin-service-edit-${s.id}`}
                          className="flex-1 text-[11px] font-display uppercase tracking-wider border border-gray-300 py-2 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => onDelete(s.id)}
                          data-testid={`admin-service-delete-${s.id}`}
                          className="px-3 border border-gray-300 hover:border-red-500 hover:text-red-500"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="admin-service-editor-modal">
          <div className="bg-white w-full max-w-2xl my-8 sm:my-0">
            <div className="bg-[#0A192F] text-white px-6 py-4 flex items-center justify-between">
              <h2 className="font-display font-bold uppercase tracking-widest text-sm">
                {editing.id ? "Edit Service" : "Add Service"}
              </h2>
              <button onClick={() => setEditing(null)} className="text-white/70 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <Label>Type</Label>
                <select
                  value={editing.kind}
                  onChange={(e) => setEditing({ ...editing, kind: e.target.value })}
                  data-testid="admin-service-input-kind"
                  className="mt-2 w-full bg-white border border-gray-300 px-4 py-3 text-[#0A192F] focus:border-[#D4AF37] focus:outline-none"
                >
                  {KINDS.map((k) => (<option key={k.v} value={k.v}>{k.label}</option>))}
                </select>
                <p className="text-[11px] text-[#0A192F]/50 mt-1">
                  "Lumber" and "Steel" appear as the two big featured cards. "Secondary" appears in the 4-up grid below.
                </p>
              </div>
              <div>
                <Label>Title</Label>
                <input
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  data-testid="admin-service-input-title"
                  className="mt-2 w-full bg-white border border-gray-300 px-4 py-3 text-[#0A192F] focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
              <div>
                <Label>Body</Label>
                <textarea
                  rows={4}
                  value={editing.body}
                  onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                  data-testid="admin-service-input-body"
                  className="mt-2 w-full bg-white border border-gray-300 px-4 py-3 text-[#0A192F] focus:border-[#D4AF37] focus:outline-none resize-y"
                />
              </div>
              {editing.kind !== "secondary" && (
                <>
                  <div>
                    <Label>Image URL</Label>
                    <div className="flex gap-2 mt-2">
                      <input
                        value={editing.image_url || ""}
                        onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
                        data-testid="admin-service-input-image"
                        className="flex-1 bg-white border border-gray-300 px-4 py-3 text-[#0A192F] focus:border-[#D4AF37] focus:outline-none"
                      />
                      <button onClick={() => setPickerOpen(true)} className="inline-flex items-center gap-1 border border-gray-300 px-3 text-xs font-display uppercase tracking-wider hover:border-[#D4AF37]">
                        <ImagePlus size={14} /> Browse
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label>Features (one per line)</Label>
                    <textarea
                      rows={6}
                      value={(editing.features || []).join("\n")}
                      onChange={(e) => setEditing({ ...editing, features: e.target.value.split("\n") })}
                      data-testid="admin-service-input-features"
                      placeholder="One bullet point per line"
                      className="mt-2 w-full bg-white border border-gray-300 px-4 py-3 text-[#0A192F] focus:border-[#D4AF37] focus:outline-none resize-y font-mono text-sm"
                    />
                  </div>
                </>
              )}
              {editing.kind === "secondary" && (
                <div>
                  <Label>Icon</Label>
                  <select
                    value={editing.icon || "Cog"}
                    onChange={(e) => setEditing({ ...editing, icon: e.target.value })}
                    data-testid="admin-service-input-icon"
                    className="mt-2 w-full bg-white border border-gray-300 px-4 py-3 text-[#0A192F] focus:border-[#D4AF37] focus:outline-none"
                  >
                    {ICONS.map((i) => (<option key={i} value={i}>{i}</option>))}
                  </select>
                </div>
              )}
              <div>
                <Label>Sort Order</Label>
                <input
                  type="number"
                  value={editing.order ?? 0}
                  onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value, 10) || 0 })}
                  className="mt-2 w-32 bg-white border border-gray-300 px-4 py-3 text-[#0A192F] focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 font-display text-xs uppercase tracking-wider text-[#0A192F]/70 hover:text-[#0A192F]">
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={saving}
                data-testid="admin-service-save"
                className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-6 py-2.5 font-display text-xs uppercase tracking-wider font-bold hover:bg-[#D4AF37] hover:text-[#0A192F] disabled:opacity-60"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {pickerOpen && (
        <MediaPicker
          onClose={() => setPickerOpen(false)}
          onSelect={(url) => { setEditing((s) => ({ ...s, image_url: url })); setPickerOpen(false); }}
        />
      )}
    </div>
  );
};

const Label = ({ children }) => (
  <label className="font-display text-[10px] uppercase tracking-[0.3em] text-[#0A192F]/70 font-semibold">
    {children}
  </label>
);

export default ServicesEditor;
